'use client';
import cn from 'classnames';
import { displayTierName } from '@/util/product_client_safe';
import React, { useEffect, useState, useRef } from 'react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { Tooltip } from 'react-tooltip';
import { FormikHelpers, FormikErrors } from 'formik';

import CircleQuestionMark from 'public/img/icon/circle-questionmark.svg';
import Close from 'public/img/icon/close.svg';

import { useModal } from '@/components/hook/modal';
import { SignupState as RegistrationValues } from '@/types/Signup';
import Button from '@/components/common/Button/Button';
import LoadingSpinner from '@/components/common/LoadingSpinner/LoadingSpinner';
import Text from '@/components/common/Text/Text';
import Form, { BaseFormProps } from '@/components/form/lib/Form/Form';
import CheckboxField from '@/components/form/lib/InputField/CheckboxField';
import SignUpSummaryModal from '@/components/modal/SignUpSummaryModal/SignUpSummaryModal';

import { CACHE_TIME } from '@/const/cache';
import { SIGNUP_ROUTES } from '@/const/signup';
import { getMembershipTaxCode } from '@/util/tax';

import { RawEPCCCartResponse } from '@/types/Cart';
import { InsuranceProduct, SubscriptionProduct } from '@/types/Subscription';

import { capitalize } from '@/util/misc';
import { USDFormatter } from '@/util/currency';

import './SignupStep5Form.style.scss';
import { ReferralCopyProps } from '@/components/common/SignUpApp/SignUpApp';

export type SignupStep5FormValues = {
  paymentAgreement: boolean;
  terms: boolean;
};

interface SignupStep5FormProps {
  user: RegistrationValues;
  errorMessage?: React.ReactNode;
  subs: SubscriptionProduct[];
  insuranceData: InsuranceProduct[];
  previewCart?: RawEPCCCartResponse;
  referralCopy?: ReferralCopyProps;
  submissionIsLoading?: boolean;
}

interface SignupStep5FormProps
  extends Omit<BaseFormProps<SignupStep5FormValues>, 'children' | 'onSubmit'> {
  onSubmit?: (
    values: SignupStep5FormValues,
    actions: FormikHelpers<SignupStep5FormValues>
  ) => void;
}

async function defaultValidation(
  values: SignupStep5FormValues
): Promise<FormikErrors<SignupStep5FormValues>> {
  const { paymentAgreement, terms } = values;
  const errors: FormikErrors<SignupStep5FormValues> = {};

  if (!paymentAgreement || !terms) {
    errors.terms = 'You must agree to all terms to continue';
  }

  return errors;
}

export default function SignupStep5Form(props: SignupStep5FormProps) {
  const {
    user,
    errorMessage,
    subs,
    insuranceData,
    previewCart,
    referralCopy,
    initialValues,
    validate = defaultValidation,
    onSubmit,
    onSubmitSuccess,
    validateOnBlur = true,
    validateOnChange = false,
    submissionIsLoading
  } = props;
  const hasUserSocial = user.instagram || user.tiktok ? true : false;
  const modalTools = useModal();
  const router = useRouter();

  const [taxRate, setTaxRate] = useState<number>(0);

  const handleSubmit = async (values: SignupStep5FormValues) => {
    onSubmitSuccess?.(values /* OptionalDataResponseHere */);
  };

  /** Total in dollars and cents */
  const subscriptionPriceWithoutTax: number = previewCart?.data?.meta
    .display_price.without_tax.amount
    ? (previewCart?.data?.meta.display_price.without_tax.amount as number) / 100
    : 0;

  const formattedTaxAmount: string = USDFormatter.format(
    subscriptionPriceWithoutTax * taxRate
  );
  const formattedTotalWithTax: string = USDFormatter.format(
    subscriptionPriceWithoutTax * (taxRate + 1)
  );

  useEffect(() => {
    const getTaxRate = async () => {
      try {
        const taxResponse = await fetch('/api/cart/tax', {
          method: 'POST',
          next: { revalidate: CACHE_TIME.NO_CACHE },
          body: JSON.stringify({
            street1: user.address,
            street2: user.aptUnitSuite || ' ',
            city: user.city,
            state: user.state,
            country: 'us',
            zip: user.postalCode,
            // Chicago shipping addresses get the lease-classed tax code so Stripe
            // returns the 15% lease tax (and exempts sales tax); everywhere else
            // keeps the default code.
            taxCode: getMembershipTaxCode({
              city: user.city,
              state: user.state
            })
          })
        });

        const json = (await taxResponse.json()) as {
          success: boolean;
          data: number;
        };

        setTaxRate(json.data);
        return json.data;
      } catch (error) {
        console.error(error);
        return;
      }
    };

    getTaxRate();
  }, [user.city, user.state, user.postalCode, user.address, user.aptUnitSuite]);

  return (
    <div className='SignupStep5Form'>
      <h1 className='Signup-step-title'>Confirm your application</h1>
      <div className='Signup-progress-bar'>
        <div
          className='Progress-bar-fill'
          style={{ width: '100%' }}
        ></div>
      </div>
      <div className='Signup-confirmation-wrapper'>
        <div className='Signup-confirmation-header'>
          <h2 className='Signup-step-subtitle'>my info</h2>
          <Button
            className='Signup-edit-button small cursor'
            variant='underline'
            onClick={() => {
              router.push(SIGNUP_ROUTES.ACCOUNT);
            }}
          >
            Edit
          </Button>
        </div>
        <div className='Signup-confirmation-info'>
          <span className='Signup-confirmation-name'>
            {`${user.firstName.trim()} ${user.lastName.trim()}`}
          </span>
          <span className='Signup-confirmation-birthday'>{user.birthday}</span>
        </div>
      </div>
      <div className='Signup-confirmation-wrapper'>
        <div className='Signup-confirmation-header'>
          <h2 className='Signup-step-subtitle'>Social Media</h2>
          <Button
            className='Signup-edit-button small cursor'
            variant='underline'
            onClick={() => {
              router.push(SIGNUP_ROUTES.PERSONALIZE);
            }}
          >
            Edit
          </Button>
        </div>
        <div className='Signup-confirmation-info'>
          {hasUserSocial ? (
            <>
              <Text>{user.instagram}</Text>
              <Text>{user.tiktok}</Text>
            </>
          ) : (
            <Text>N/A</Text>
          )}
        </div>
      </div>
      <div className='Signup-confirmation-wrapper'>
        <div className='Signup-confirmation-header'>
          <h2 className='Signup-step-subtitle'>My shipping address</h2>
          <Button
            className='Signup-edit-button small cursor'
            variant='underline'
            onClick={() => {
              router.push(SIGNUP_ROUTES.ADDRESS);
            }}
          >
            Edit
          </Button>
        </div>
        <div className='Signup-confirmation-info'>
          <span className='Signup-confirmation-shipping-address'>
            {user.address}, {user.aptUnitSuite ? `${user.aptUnitSuite}, ` : ''}{' '}
            {user.city}, {user.state}, {user.postalCode}
          </span>
        </div>
      </div>
      <div className='Signup-confirmation-billing'>
        <div className='Signup-confirmation-header'>
          <h2 className='Signup-step-subtitle'>My Billing information</h2>
          <Button
            className='Signup-edit-button small cursor'
            variant='underline'
            onClick={() => {
              router.push(SIGNUP_ROUTES.BILLING);
            }}
          >
            Edit
          </Button>
        </div>
        <div className='Signup-confirmation-info'>
          <span className='Signup-confirmation-billing-card'>
            {user.stripePaymentMethodBrand.toUpperCase()} ending in{' '}
            {user.creditCardLast4}
          </span>
          <span className='Signup-confirmation-billing-address'>
            {user.sameAsShippingAddress && (
              <>
                {user.address},{' '}
                {user.aptUnitSuite ? `${user.aptUnitSuite}, ` : ''} {user.city},{' '}
                {user.state}, {user.postalCode}
              </>
            )}
            {!user.sameAsShippingAddress && (
              <>
                {user.billingAddress},{' '}
                {user.billingAptUnitSuite
                  ? `${user.billingAptUnitSuite}, `
                  : ''}{' '}
                {user.billingCity}, {user.billingState},{' '}
                {user.billingPostalCode}
              </>
            )}
          </span>
        </div>
      </div>

      <div className='Signup-confirmation-bottom-details'>
        <div className='membership details'>
          <div className='title-button-wrapper'>
            <h2 className='Signup-step-subtitle'>Membership details</h2>
            <Button
              className='Signup-edit-button small cursor'
              variant='underline'
              onClick={() => {
                modalTools.showModal(
                  <SignUpSummaryModal
                    subs={subs}
                    insuranceData={insuranceData}
                    onClose={() => {
                      modalTools.closeModal();
                    }}
                    user={user}
                  />,
                  {
                    isFullScreen: true,
                    anchorPoint: 'bottom'
                  }
                );
              }}
            >
              Edit
            </Button>
          </div>
          <div className='info-wrapper'>
            <span className='info-label'>Tier:</span>
            {displayTierName(user.plan?.name ?? '')}
            {user.addon_subscription === 'premier' && ', Premier add-on'}
          </div>
          <div className='info-wrapper'>
            <span className='info-label'>Billing Cycle: </span>
            Monthly
          </div>
          <div className='info-wrapper'>
            <span className='info-label'>Coverage: </span>
            {capitalize(user.insurance_level)}
          </div>
          <div className='info-wrapper'>
            <span className='info-label'>Referral: </span>
            {user.referralCode ? (
              <>
                {user.referralCode}
                <Text
                  className={cn({
                    'InputField-error-text':
                      referralCopy?.copy && !referralCopy?.successfullyApplied
                  })}
                  variant='text-s'
                >
                  {referralCopy?.copy}
                </Text>
              </>
            ) : (
              <>None</>
            )}
          </div>
        </div>
        <div className='billing details'>
          <div className='title-button-wrapper'>
            <div className='Signup-billing-title-wrapper'>
              <h2 className='Signup-step-subtitle'>Billing summary</h2>
              <button className='Signup-tooltip-anchor cursor'>
                <Image
                  className=''
                  src={CircleQuestionMark}
                  alt=''
                />
              </button>

              <Tooltip
                anchorSelect='.Signup-tooltip-anchor'
                className='Signup-tooltip cursor'
                classNameArrow='Signup-tooltip-arrow'
                opacity={1}
                place='top'
              >
                <Image
                  className='close'
                  src={Close}
                  alt=''
                  style={{
                    paddingLeft: 14,
                    display: 'flex',
                    float: 'right'
                  }}
                />
                <Text
                  variant='title-xxs-500'
                  uppercase
                >
                  Since $0 is due now, when will I be charged?
                </Text>
                Your billing cycle does not start until your membership
                application has been reviewed by our team and accepted.
                <Text
                  variant='title-xxs-500'
                  uppercase
                >
                  How is tax calculated?
                </Text>
                Tax is estimated based on your shipping address inputted at the
                time of application.
              </Tooltip>
            </div>
          </div>
          <div className='billing-wrapper'>
            <Text
              variant='title-xxs-500'
              uppercase
            >
              Membership:
            </Text>
            <Text variant='text-m'>
              {previewCart?.data?.meta.display_price.without_discount.formatted}
            </Text>
          </div>
          {previewCart?.data?.meta.display_price.discount.amount !== 0 && (
            <div className='billing-wrapper discount'>
              <Text
                variant='title-xxs-500'
                uppercase
              >
                Discount:
              </Text>
              <Text variant='text-m'>
                {previewCart?.data?.meta.display_price.discount.formatted}
              </Text>
            </div>
          )}
          <div className='billing-wrapper'>
            <Text
              variant='title-xxs-500'
              uppercase
            >
              Estimated tax:
            </Text>
            <Text variant='text-m'>
              {taxRate >= 0 ? formattedTaxAmount : <LoadingSpinner size={10} />}
            </Text>
          </div>
          <div className='billing-wrapper total'>
            <Text
              variant='title-xxs-700-upper'
              uppercase
            >
              Monthly total:
            </Text>
            <Text
              variant='title-xxs-700-upper'
              uppercase
            >
              {taxRate >= 0 ? (
                formattedTotalWithTax
              ) : (
                <LoadingSpinner size={10} />
              )}
            </Text>
          </div>
          <div className='due-now billing-info'>
            <div className='billing-wrapper'>
              <Text
                variant='title-xxs-700-upper'
                className='billing-wrapper-total'
              >
                Due Now:
              </Text>
              <Text variant='title-xxs-700-upper'>$0</Text>
            </div>
          </div>
        </div>
      </div>
      <Form<SignupStep5FormValues>
        initialValues={initialValues}
        validate={validate}
        // If an 'onSubmit function' is passed in, that function will override
        // this component's 'handleSubmit' function.
        onSubmit={onSubmit || handleSubmit}
        validateOnBlur={validateOnBlur}
        validateOnChange={validateOnChange}
      >
        <div className='Optional-checkboxes'>
          <div className='InputField-checkbox'>
            <CheckboxField
              label={
                'I understand that my membership will start once approved, and I agree to pay for 3 months of membership fees if I decide to cancel before then.'
              }
              name='paymentAgreement'
            />
          </div>
          <div className='InputField-checkbox'>
            <CheckboxField
              label={
                <span>
                  By clicking here, I have read and hereby agree to the{' '}
                  <a
                    href='/terms'
                    className='Signup-terms'
                    target='_blank'
                  >
                    terms of use
                  </a>
                  , and I acknowledge that I have read the{' '}
                  <a
                    href='/privacy-policy'
                    className='Signup-terms'
                    target='_blank'
                  >
                    privacy policy
                  </a>
                  . I acknowledge that the information entered in this form may
                  be utilized to assess my eligibility for Vivrelle membership,
                  either through a background check or the verification of the
                  identity I have provided herein.
                </span>
              }
              name='terms'
            />
          </div>
        </div>
        <button
          className={cn('Signup-Continue-Button cursor', {
            disabled: submissionIsLoading
          })}
          disabled={submissionIsLoading || !!errorMessage}
          type='submit'
        >
          {submissionIsLoading ? (
            <>
              Submitting <LoadingSpinner size={25} />
            </>
          ) : (
            'Submit Application'
          )}
        </button>
        {errorMessage && <Text className='error'>{errorMessage}</Text>}
      </Form>
    </div>
  );
}
